import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { PartyPopper, CalendarHeart, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}
function daysBetween(fromISO, toISO) {
  const a = new Date(fromISO + "T00:00:00");
  const b = new Date(toISO + "T00:00:00");
  return Math.round((b - a) / 86_400_000);
}
function pickNext(rows) {
  const today = todayISO();
  const up = (rows || [])
    .filter((r) => (r.end_date || r.start_date) >= today)
    .sort((a, b) => a.start_date.localeCompare(b.start_date));
  return up[0] || null;
}

// Rotating "what's next" card — cycles through the next holiday and next event
// one slide at a time. `holidayEndpoint`/`eventEndpoint` are role-specific.
export default function NextUpBanner({
  holidayEndpoint = "/holidays", holidayTo = "/holidays",
  eventEndpoint = "/events", eventTo = "/events",
  showHoliday = true, showEvent = true,
}) {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const built = [];
      const year = new Date().getFullYear();
      if (showHoliday) {
        try {
          const { data } = await api.get(holidayEndpoint, { params: { year } });
          const h = pickNext(data.holidays);
          if (h) built.push({ kind: "holiday", data: h, to: holidayTo });
        } catch { /* silent */ }
      }
      if (showEvent) {
        try {
          const { data } = await api.get(eventEndpoint, { params: { year } });
          const e = pickNext(data.events);
          if (e) built.push({ kind: "event", data: e, to: eventTo });
        } catch { /* silent */ }
      }
      if (!cancelled) { setSlides(built); setIdx(0); }
    })();
    return () => { cancelled = true; };
  }, [holidayEndpoint, eventEndpoint, holidayTo, eventTo, showHoliday, showEvent]);

  useEffect(() => {
    if (slides.length <= 1) return;
    timer.current = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(timer.current);
  }, [slides.length]);

  if (!slides.length) return null;
  const s = slides[idx];
  const today = todayISO();
  const d = s.data;
  const ongoing = d.start_date <= today;
  const inDays = daysBetween(today, d.start_date);
  const isHoliday = s.kind === "holiday";
  const when = ongoing ? (isHoliday ? "Today" : "Happening now") : inDays === 1 ? "Tomorrow" : `In ${inDays} days`;
  const title = isHoliday
    ? `${ongoing ? "Holiday today" : "Next holiday"} — ${d.name}`
    : `${ongoing ? "Event today" : "Next event"} — ${d.title}`;
  const sub = isHoliday
    ? `${when} · ${d.type}`
    : `${when} · ${d.type}${d.venue ? ` · ${d.venue}` : ""}`;

  const theme = isHoliday
    ? { border: "border-rose-200 dark:border-rose-900/50", bg: "from-rose-50 to-amber-50 dark:from-rose-950/30 dark:to-amber-950/20", ic: "text-rose-600 dark:text-rose-300", arr: "text-rose-500", subc: "text-rose-700/80 dark:text-rose-300/80", dot: "bg-rose-500" }
    : { border: "border-indigo-200 dark:border-indigo-900/50", bg: "from-indigo-50 to-sky-50 dark:from-indigo-950/30 dark:to-sky-950/20", ic: "text-indigo-600 dark:text-indigo-300", arr: "text-indigo-500", subc: "text-indigo-700/80 dark:text-indigo-300/80", dot: "bg-indigo-500" };
  const Icon = isHoliday ? PartyPopper : CalendarHeart;

  return (
    <div className="max-w-full sm:max-w-[19rem]" data-testid="next-up-banner">
      <button
        type="button"
        onClick={() => navigate(s.to)}
        key={s.kind + idx}
        className={`nextup-slide w-full flex items-center gap-2.5 rounded-xl border ${theme.border} bg-gradient-to-r ${theme.bg} px-3 py-1.5 text-left hover:shadow-sm transition-shadow group`}
        data-testid={`next-up-slide-${s.kind}`}
      >
        <div className={`h-7 w-7 rounded-lg bg-white/70 dark:bg-slate-800 ${theme.ic} flex items-center justify-center shrink-0`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">{title}</div>
          <div className={`text-[11px] ${theme.subc} truncate`}>{sub}</div>
        </div>
        <ArrowRight className={`h-3.5 w-3.5 ${theme.arr} shrink-0 group-hover:translate-x-0.5 transition-transform`} />
      </button>
      {slides.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-1.5" data-testid="next-up-dots">
          {slides.map((sl, i) => (
            <button
              key={sl.kind}
              type="button"
              aria-label={`Show ${sl.kind}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? `w-4 ${theme.dot}` : "w-1.5 bg-slate-300 dark:bg-slate-600"}`}
              data-testid={`next-up-dot-${i}`}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes nextup-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .nextup-slide { animation: nextup-in 0.4s ease; }
      `}</style>
    </div>
  );
}
